'use client';

import React from 'react';
import styled from 'styled-components';
import { MetricInfoIcon, InfoTooltipIcon, ImageIcon } from '@/components/icons';
import { Tooltip } from '@/components/ui-components/Tooltip';

// ============================================================================
// Types
// ============================================================================

export interface WebVitalMetric {
  acronym: string;
  fullName: string;
  name: string;
  value: number;
  unit: string;
  status: 'good' | 'caution' | 'critical';
  min: number;
  max: number;
  goodThreshold: number;
  cautionThreshold: number;
  screenshot: string | null;
}

export interface WebVitalMetricCardProps {
  metric: WebVitalMetric;
  onInfoClick?: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const MetricCard = styled.div<{ $status: 'good' | 'caution' | 'critical' }>`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ $status, theme }) => {
    switch ($status) {
      case 'good': return theme.colors.status.success;
      case 'caution': return theme.colors.status.warning;
      case 'critical': return theme.colors.status.error;
    }
  }};
  border-radius: 8px;
  padding: 24px 15px;
  display: flex;
  flex-direction: column;
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const MetricInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  gap: 12px;
`;


const MetricTextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetricAcronym = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray[900]};
  font-family: 'Open Sans', sans-serif;
  text-transform: uppercase;
  line-height: 27px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricName = styled.p`
  font-size: 12px;
  line-height: 100%;
  color: ${({ theme }) => theme.colors.gray[550]};
  font-family: 'Open Sans', sans-serif;
  text-transform: uppercase;
`;

const MetricIconButton = styled.button`
  display: flex;
  align-items: center;
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  
  &:focus {
    outline: none;
  }
  
`;

const MetricValueContainer = styled.div`
display: flex;
align-items: center;
justify-content: space-between;
gap: 10px;
`;

// Base component with shared status color logic
const BaseMetricText = styled.div<{ $status: 'good' | 'caution' | 'critical' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $status, theme }) => {
    switch ($status) {
      case 'good': return theme.colors.status.success;
      case 'caution': return theme.colors.status.warning;
      case 'critical': return theme.colors.status.error;
    }
  }};
`;

const MetricValue = styled(BaseMetricText)`
  margin-bottom: 6px;
`;

const MetricStatus = styled(BaseMetricText)`
  text-transform: uppercase;
`;

const MetricSlider = styled.div`
  position: relative;
  height: 10px;
  border-radius: 4px;
  overflow: visible;
  background: linear-gradient(
    to right,
    #10b981 0%,
    #10b981 33.33%,
    #fbbf24 33.33%,
    #fbbf24 66.66%,
    #ef4444 66.66%,
    #ef4444 100%
  );
  margin-bottom: 8px;
`;

const MetricSliderThumb = styled.div<{ $position: number; $status: 'good' | 'caution' | 'critical' }>`
  position: absolute;
  top: 50%;
  left: ${({ $position }) => $position}%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  border: 2px solid ${({ $status }) => {
    switch ($status) {
      case 'good': return '#10b981';
      case 'caution': return '#f59e0b';
      case 'critical': return '#ef4444';
    }
  }};
`;

const MetricLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #9ca3af;
`;

// ============================================================================
// Component
// ============================================================================

/**
 * WebVitalMetricCard Component
 * 
 * Displays a Core Web Vitals performance metric with visual indicators.
 * Shows metric value, status (good/caution/critical), and a color-coded slider.
 * 
 * **Features:**
 * - Smart decimal formatting: Shows decimals only when non-zero (e.g., "2.5s" vs "3s")
 * - Supports seconds (s), milliseconds (ms), and unitless metrics (CLS)
 * - Color-coded status indicators (good/caution/critical)
 * - Visual slider with threshold markers
 * - Tooltip with full metric name
 * 
 * @component
 * @example
 * ```tsx
 * // Time-based metric (seconds)
 * const lcpMetric: WebVitalMetric = {
 *   acronym: 'LCP',
 *   fullName: 'Largest Contentful Paint',
 *   name: 'Perceived Loading Speed',
 *   value: 2.5,
 *   unit: 's',
 *   status: 'good',
 *   min: 0,
 *   max: 4,
 *   goodThreshold: 2.5,
 *   cautionThreshold: 4
 * };
 * 
 * // Unitless metric (CLS)
 * const clsMetric: WebVitalMetric = {
 *   acronym: 'CLS',
 *   fullName: 'Cumulative Layout Shift',
 *   name: 'Visual Stability',
 *   value: 0.12,
 *   unit: '',
 *   status: 'good',
 *   min: 0,
 *   max: 0.25,
 *   goodThreshold: 0.1,
 *   cautionThreshold: 0.25
 * };
 * 
 * <WebVitalMetricCard 
 *   metric={lcpMetric}
 *   onInfoClick={() => console.log('Info clicked')}
 * />
 * ```
 */
export const WebVitalMetricCard: React.FC<WebVitalMetricCardProps> = ({ 
  metric,
  onInfoClick 
}) => {
  // Calculate position percentage for the slider thumb based on actual value
  // The slider has three segments: green (0-33.33%), yellow (33.33-66.66%), red (66.66-100%)
  const calculatePosition = () => {
    // Handle undefined or null values
    if (metric.value === undefined || metric.value === null || isNaN(metric.value)) {
      return 0;
    }
    
    // Handle edge cases for thresholds
    if (metric.min === undefined || metric.min === null || isNaN(metric.min)) {
      return 0;
    }
    if (metric.max === undefined || metric.max === null || isNaN(metric.max)) {
      return 0;
    }
    if (metric.goodThreshold === undefined || metric.goodThreshold === null || isNaN(metric.goodThreshold)) {
      return 0;
    }
    if (metric.cautionThreshold === undefined || metric.cautionThreshold === null || isNaN(metric.cautionThreshold)) {
      return 0;
    }
    
    // If min equals max, position at 0%
    if (metric.max === metric.min) {
      return 0;
    }
    
    const value = metric.value;
    const min = metric.min;
    const goodThreshold = metric.goodThreshold;
    const cautionThreshold = metric.cautionThreshold;
    const max = metric.max;
    
    // Each segment represents 33.33% of the slider
    const segmentSize = 33.33;
    
    // Green segment: min to goodThreshold (0% to 33.33%)
    if (value <= goodThreshold) {
      // If goodThreshold equals min, green segment has no range - position at start
      if (goodThreshold === min) {
        return 0;
      }
      const position = ((value - min) / (goodThreshold - min)) * segmentSize;
      return Math.max(0, Math.min(segmentSize, position));
    }
    
    // Yellow segment: goodThreshold to cautionThreshold (33.33% to 66.66%)
    if (value <= cautionThreshold) {
      // If cautionThreshold equals goodThreshold, yellow segment has no range - skip to red
      if (cautionThreshold === goodThreshold) {
        return segmentSize * 2;
      }
      const position = segmentSize + ((value - goodThreshold) / (cautionThreshold - goodThreshold)) * segmentSize;
      return Math.max(segmentSize, Math.min(segmentSize * 2, position));
    }
    
    // Red segment: cautionThreshold to max (66.66% to 100%)
    // If max equals cautionThreshold, red segment has no range - position at end
    if (max === cautionThreshold) {
      return 100;
    }
    const position = segmentSize * 2 + ((value - cautionThreshold) / (max - cautionThreshold)) * segmentSize;
    return Math.max(segmentSize * 2, Math.min(100, position));
  };


  const formatValue = (value: number, unit: string) => {
    // Handle undefined or null values
    if (value === undefined || value === null || isNaN(value)) {
      return 'N/A';
    }
    if (unit === 'ms') {
      // Round milliseconds to whole numbers
      return `${Math.round(value)}ms`;
    }
    if (unit === 's') {
      // Format seconds, remove trailing zeros
      const formatted = parseFloat(value.toFixed(2));
      return `${formatted}s`;
    }
    // For unitless metrics (like CLS), format to max 2 decimal places, remove trailing zeros
    return parseFloat(value.toFixed(2)).toString();
  };

  const getStatusLabel = () => {
    switch (metric.status) {
      case 'good': return 'Good';
      case 'caution': return 'Caution';
      case 'critical': return 'Critical';
    }
  };

  const getMinLabel = () => {
    if (metric.min === undefined || metric.min === null || isNaN(metric.min)) {
      return 'N/A';
    }
    if (metric.unit === 'ms') {
      return `${Math.round(metric.min)}ms`;
    }
    if (metric.unit === 's') {
      return `${parseFloat(metric.min.toFixed(2))}s`;
    }
    return parseFloat(metric.min.toFixed(2)).toString();
  };

  const getGoodLabel = () => {
    if (metric.goodThreshold === undefined || metric.goodThreshold === null || isNaN(metric.goodThreshold)) {
      return 'N/A';
    }
    if (metric.unit === 'ms') {
      return `${Math.round(metric.goodThreshold)}ms`;
    }
    if (metric.unit === 's') {
      return `${parseFloat(metric.goodThreshold.toFixed(2))}s`;
    }
    return parseFloat(metric.goodThreshold.toFixed(2)).toString();
  };

  const getCautionLabel = () => {
    if (metric.cautionThreshold === undefined || metric.cautionThreshold === null || isNaN(metric.cautionThreshold)) {
      return 'N/A';
    }
    if (metric.unit === 'ms') {
      return `${Math.round(metric.cautionThreshold)}ms`;
    }
    if (metric.unit === 's') {
      return `${parseFloat(metric.cautionThreshold.toFixed(2))}s`;
    }
    return parseFloat(metric.cautionThreshold.toFixed(2)).toString();
  };

  const getMaxLabel = () => {
    if (metric.max === undefined || metric.max === null || isNaN(metric.max)) {
      return 'N/A';
    }
    if (metric.unit === 'ms') {
      return `${Math.round(metric.max)}ms`;
    }
    if (metric.unit === 's') {
      return `${parseFloat(metric.max.toFixed(2))}s`;
    }
    return parseFloat(metric.max.toFixed(2)).toString();
  };

  const thumbPosition = calculatePosition();

  return (
    <MetricCard $status={metric.status}>
      <MetricHeader>
        <MetricInfo>
          <MetricTextContainer>
            <MetricAcronym>
              {metric.acronym}
              <Tooltip title={metric.fullName} position="top">
                <InfoTooltipIcon size={12} color="#6E6E6E" />
              </Tooltip>
              {metric.screenshot && (
          <MetricIconButton onClick={() => window.open(metric.screenshot||'', '_blank')} type="button" aria-label="View screenshot">
            <ImageIcon/>
          </MetricIconButton>
        )}
            </MetricAcronym>
            <MetricName>{metric.name}</MetricName>
          </MetricTextContainer>
        </MetricInfo>
        {/* comment out for now until we have a way to display the metric info */}
        {/* <MetricIconButton onClick={onInfoClick} type="button" aria-label="More information">
          <MetricInfoIcon size={24} />
        </MetricIconButton> */}
      </MetricHeader>

      <MetricValueContainer>
        <MetricValue $status={metric.status}>
          {formatValue(metric.value, metric.unit)}
        </MetricValue>
        <MetricStatus $status={metric.status}>
          {getStatusLabel()}
        </MetricStatus>
      </MetricValueContainer>

      <MetricSlider>
        <MetricSliderThumb $position={thumbPosition} $status={metric.status} />
      </MetricSlider>

      <MetricLabels>
        <span>{getMinLabel()}</span>
        <span>{getGoodLabel()}</span>
        <span>{getCautionLabel()}</span>
        <span>{getMaxLabel()}</span>
      </MetricLabels>
    </MetricCard>
  );
};

export default WebVitalMetricCard;

